/**
 * Canonical route-access decisions for auth-gated mobile surfaces.
 *
 * Centralizes:
 * - which routes anonymous users may access
 * - which routes must not mount while auth is still resolving
 * - which redirect target should win once auth is known
 */

const ANON_ALLOWED_TABS = new Set(["index", "history", "stats", "settings"]);

export interface AuthRouteAccessInput {
	segments: string[];
	loading: boolean;
	isAnonymous: boolean;
	hasAccessToken: boolean;
}

export interface AuthRouteAccessDecision {
	tab?: string;
	shouldRenderGate: boolean;
	shouldBlockProtectedMount: boolean;
	redirectTo: "/login" | "/signup?source=anon_premium_tab" | "/" | null;
}

export function getAuthRouteAccessDecision({
	segments,
	loading,
	isAnonymous,
	hasAccessToken,
}: AuthRouteAccessInput): AuthRouteAccessDecision {
	const inAuthGroup = segments[0] === "(auth)";
	const inTabsGroup = segments[0] === "(tabs)";
	const inPractitionerGroup = segments[0] === "(practitioner)";
	const onHomeScreen = inTabsGroup && segments.length === 1;
	const tab = inTabsGroup ? segments[1] : undefined;
	const isAnonAllowedTab =
		tab === undefined || tab === "index" || ANON_ALLOWED_TABS.has(tab);

	const isPostAnalysisRoute =
		segments.includes("analyze") || segments.includes("results");
	const isSharedRoute = segments[0] === "s";
	// Recovery path — always accessible regardless of auth state (three-surface architecture)
	const isHelpRoute = segments[0] === "how-it-works";

	const isAnonAllowedRoute =
		onHomeScreen ||
		inAuthGroup ||
		isPostAnalysisRoute ||
		isSharedRoute ||
		isHelpRoute ||
		(inTabsGroup && isAnonAllowedTab);

	const shouldBlockProtectedMount =
		loading &&
		!inAuthGroup &&
		!isPostAnalysisRoute &&
		!isSharedRoute &&
		!isHelpRoute &&
		(inPractitionerGroup || (inTabsGroup && !isAnonAllowedTab));

	let redirectTo: AuthRouteAccessDecision["redirectTo"] = null;

	if (!loading && isAnonymous && !isAnonAllowedRoute) {
		redirectTo = "/signup?source=anon_premium_tab";
	} else if (
		!loading &&
		!isAnonymous &&
		!hasAccessToken &&
		!isAnonAllowedRoute
	) {
		redirectTo = "/login";
	} else if (!loading && !isAnonymous && hasAccessToken && inAuthGroup) {
		redirectTo = "/";
	}

	return {
		tab,
		shouldBlockProtectedMount,
		shouldRenderGate: shouldBlockProtectedMount || redirectTo !== null,
		redirectTo,
	};
}
